import React from 'react';
import { motion } from 'framer-motion';
import { CodeIcon, MapPinIcon, DatabaseIcon, SparklesIcon, UserSearchIcon, MegaphoneIcon, MailIcon, ShoppingCartIcon, ArrowRightIcon } from 'lucide-react';
const features = [{
  title: 'Local business API',
  icon: CodeIcon,
  category: 'Find',
  description: 'The premier local business data API. Search, filter, and access 200M+ local businesses programmatically.',
  href: '#/features'
}, {
  title: 'Google Maps scraper',
  icon: MapPinIcon,
  category: 'Find',
  description: 'Extract business data from Google Maps at scale — contacts, ratings, reviews, hours, and more.',
  href: '#/google-maps-scraper'
}, {
  title: 'Local business database',
  icon: DatabaseIcon,
  category: 'Find',
  description: 'Download ready-to-use business datasets by category and location. Pre-built datasets updated weekly.',
  href: '#/local-business-database'
}, {
  title: 'Local business enrichment',
  icon: SparklesIcon,
  category: 'Enrich',
  description: 'The premier local business data API. Search, filter, and access 200M+ local businesses programmatically.',
  href: '#/local-business-enrichment'
}, {
  title: 'Local business owner finder',
  icon: UserSearchIcon,
  category: 'Enrich',
  description: 'Find verified decision-maker contacts at any local business. Direct emails, phone numbers, an...',
  href: '#/owner-finder'
}, {
  title: 'Local marketing',
  icon: MegaphoneIcon,
  category: 'Outreach',
  description: 'Build targeted local business audiences for Meta Ads and direct mail campaigns.',
  href: '#/local-marketing'
}, {
  title: 'Email sequencing',
  icon: MailIcon,
  category: 'Outreach',
  description: 'Create automated multi-step B2B email sequences with built-in personalization and reply tracking.',
  href: '#/email-sequencing'
}, {
  title: 'E-commerce database',
  icon: ShoppingCartIcon,
  category: 'Find',
  description: 'Search 14M+ e-commerce stores with verified owner emails, platform data, and revenue insights.',
  href: '#/ecommerce-database'
}];
const categoryStyles: Record<string, string> = {
  Find: 'bg-blue-50 text-blue-700',
  Enrich: 'bg-green-50 text-green-700',
  Outreach: 'bg-purple-50 text-purple-700'
};
export function CoreFeatures() {
  return <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24" data-id="element-2636">
      <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 p-8 md:p-12 relative overflow-hidden" data-id="element-2637">
        {/* Ambient floating blob */}
        <motion.div animate={{
        x: [0, -35, 25, 0],
        y: [0, 20, -30, 0],
        scale: [1, 1.2, 0.9, 1]
      }} transition={{
        duration: 16,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="absolute bottom-[-15%] left-[-10%] w-[35%] h-[35%] rounded-full bg-blue-100 blur-[90px] opacity-40 pointer-events-none" data-id="element-2638" />
        <motion.h2 initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 0.5
      }} className="text-3xl md:text-4xl font-bold text-slate-900 mb-10 text-left" data-id="element-2639">
          Openmart <span className="text-blue-600" data-id="element-2640">core features</span>
        </motion.h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-2641">
          {features.map((feature, index) => {
          const Icon = feature.icon;
          return <motion.a key={index} href={feature.href} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.5,
            delay: index * 0.08
          }} className="group flex flex-col bg-white rounded-2xl border border-slate-200 p-7 hover:-translate-y-2 hover:shadow-2xl hover:shadow-blue-600/10 hover:border-blue-200 transition-all duration-300 relative overflow-hidden" data-id="element-2642">
                <div className="absolute inset-0 bg-blue-50/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" data-id="element-2643" />

                <div className="w-12 h-12 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center mb-6 relative z-10" data-id="element-2644">
                  <Icon className="w-6 h-6 text-slate-700 group-hover:text-blue-600 transition-colors" data-id="element-2645" />
                </div>

                <h3 className="text-lg font-bold text-slate-900 mb-3 relative z-10" data-id="element-2646">
                  {feature.title}
                </h3>

                <div className="mb-4 relative z-10" data-id="element-2647">
                  <span className={`px-3 py-1 rounded-lg text-xs font-semibold ${categoryStyles[feature.category]}`} data-id="element-2648">
                    {feature.category}
                  </span>
                </div>

                <p className="text-sm text-slate-500 leading-relaxed mb-6 relative z-10 flex-grow" data-id="element-2649">
                  {feature.description}
                </p>

                <div className="border-t border-slate-100 pt-5 mt-auto flex items-center text-sm font-semibold text-blue-500 group-hover:text-blue-600 transition-colors relative z-10" data-id="element-2650">
                  Learn more{' '}
                  <ArrowRightIcon className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" data-id="element-2651" />
                </div>
              </motion.a>;
        })}
        </div>
      </div>
    </section>;
}